import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import {
  ROTULO_DE_STATUS,
  type CategoriaDeStatus,
  type Nivel,
  type TipoDeChamado,
} from '@/features/tickets/types';

/**
 * Etiquetas do domínio de chamado.
 *
 * Separadas do `StatusPill` de propósito: aquele fala do autoteste
 * (OK/atenção/falha) e ganhar um sexto significado o tornaria genérico demais
 * para dizer qualquer coisa.
 *
 * Toda etiqueta traz **a palavra**, nunca só a cor — prioridade crítica
 * precisa ser legível para quem não distingue vermelho de verde.
 */

export function Etiqueta({
  className,
  title,
  children,
}: {
  className?: string;
  /**
   * O que a etiqueta significa, em uma frase.
   *
   * Existe porque uma etiqueta boa é curta, e curto às vezes é ambíguo:
   * "Aprovado" num artigo de conhecimento não quer dizer "no ar". O rótulo dá
   * o nome; o `title` diz o efeito.
   */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5',
        'text-xs font-semibold ring-1 ring-inset',
        className,
      )}
      title={title}
    >
      {children}
    </span>
  );
}

const ESTILO_DE_STATUS: Record<CategoriaDeStatus, string> = {
  NEW: 'bg-primary/12 text-primary ring-primary/25',
  IN_PROGRESS: 'bg-primary/12 text-primary ring-primary/25',
  PENDING: 'bg-warning/15 text-warning ring-warning/30',
  RESOLVED: 'bg-success/12 text-success ring-success/25',
  CLOSED: 'bg-muted text-muted-foreground ring-border',
  CANCELLED: 'bg-muted text-muted-foreground ring-border line-through',
};

export function EtiquetaDeStatus({ status }: { status: CategoriaDeStatus }) {
  return <Etiqueta className={ESTILO_DE_STATUS[status]}>{ROTULO_DE_STATUS[status]}</Etiqueta>;
}

const ESTILO_DE_PRIORIDADE: Record<Nivel, string> = {
  LOW: 'bg-muted text-muted-foreground ring-border',
  MEDIUM: 'bg-secondary text-secondary-foreground ring-border',
  HIGH: 'bg-warning/15 text-warning ring-warning/30',
  CRITICAL: 'bg-destructive/12 text-destructive ring-destructive/25',
};

export function EtiquetaDePrioridade({
  prioridade,
  label,
  sobrescrita = false,
}: {
  prioridade: Nivel;
  /** Rótulo configurado pelo tenant. Se ausente, mostra o valor do enum. */
  label?: string;
  sobrescrita?: boolean;
}) {
  return (
    <Etiqueta className={ESTILO_DE_PRIORIDADE[prioridade]}>
      {label ?? prioridade}
      {/* A matriz impacto × urgência foi contrariada à mão; a tela mostra isso
          porque senão o número parece derivado quando não é. */}
      {sobrescrita ? <span title="Prioridade definida à mão">&nbsp;·&nbsp;manual</span> : null}
    </Etiqueta>
  );
}

export function EtiquetaDeTipo({
  tipo,
  label,
}: {
  tipo: TipoDeChamado;
  /** Rótulo configurado pelo tenant. Se ausente, mostra o valor do enum. */
  label?: string;
}) {
  return (
    <Etiqueta className="bg-accent text-foreground/80 ring-border">{label ?? tipo}</Etiqueta>
  );
}

/**
 * Badge do nível de suporte (N1 / N2 / N3).
 *
 * N1 aparece em cinza discreto — é o estado normal; N2+ aparece em âmbar para
 * sinalizar que o chamado foi escalado. Quando `tier` é nulo e houve
 * escalonamentos, recorre ao contador para derivar o nível aproximado.
 */
export function BadgeDeTier({
  tier,
  escalacoes = 0,
}: {
  tier: number | null;
  escalacoes?: number;
}) {
  const nivel = tier ?? (escalacoes > 0 ? 1 + escalacoes : null);
  if (nivel == null) return null;

  const escalado = nivel > 1;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
        escalado
          ? 'bg-warning/15 text-warning ring-warning/30'
          : 'bg-muted text-muted-foreground ring-border'
      }`}
      title={escalado ? `Escalonado para N${nivel}` : 'First Line (N1)'}
    >
      N{nivel}
    </span>
  );
}

/**
 * Barra de progresso consolidada do pai.
 *
 * `aria-*` de `progressbar` porque a barra é a informação, não decoração: sem
 * isso o leitor de tela anuncia só o número solto ao lado.
 */
export function BarraDeProgresso({
  percentual,
  className,
}: {
  percentual: number;
  className?: string;
}) {
  const valor = Math.max(0, Math.min(100, Math.round(percentual)));
  return (
    <div
      role="progressbar"
      aria-valuenow={valor}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Progresso dos subchamados"
      className={cn('h-2 w-full overflow-hidden rounded-full bg-muted', className)}
    >
      <div
        className="h-full rounded-full gradient-primary transition-[width] duration-300"
        style={{ width: `${valor}%` }}
      />
    </div>
  );
}
